/**
 * React hooks for comments
 * Integrates with TanStack Query for caching and state management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as commentsService from '../services/comments';
import { postKeys } from './usePosts';

// Query keys for consistent cache management
export const commentKeys = {
  all: ['comments'] as const,
  lists: () => [...commentKeys.all, 'list'] as const,
  list: (postId: number) => [...commentKeys.lists(), postId] as const,
};

/**
 * Fetch all comments for a specific post
 */
export function useComments(postId: number) {
  return useQuery({
    queryKey: commentKeys.list(postId),
    queryFn: () => commentsService.getCommentsByPostId(postId),
    enabled: !!postId,
  });
}

/**
 * Create a new comment
 */
export function useCreateComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      postId,
      userId,
      content,
    }: {
      postId: number;
      userId: string;
      content: string;
    }) => commentsService.createComment(postId, userId, content),
    onSuccess: (_, variables) => {
      // Invalidate comments list for this post to refresh comments
      queryClient.invalidateQueries({
        queryKey: commentKeys.list(variables.postId),
      });
      // Invalidate posts list to update comment counts
      queryClient.invalidateQueries({
        queryKey: postKeys.lists(),
      });
      // Invalidate the specific post detail to update comment count
      queryClient.invalidateQueries({
        queryKey: postKeys.detail(variables.postId),
      });
    },
  });
}

/**
 * Delete a comment
 */
export function useDeleteComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId }: { commentId: number; postId: number }) =>
      commentsService.deleteComment(commentId),
    onSuccess: (_, variables) => {
      // Invalidate comments list for this post to refresh comments
      queryClient.invalidateQueries({
        queryKey: commentKeys.list(variables.postId),
      });
      // Invalidate posts list to update comment counts
      queryClient.invalidateQueries({
        queryKey: postKeys.lists(),
      });
      // Invalidate the specific post detail to update comment count
      queryClient.invalidateQueries({
        queryKey: postKeys.detail(variables.postId),
      });
    },
  });
}

